import * as PopoverPrimitive from "@radix-ui/react-popover";
import { AnimatePresence, m } from "framer-motion";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "../cn";
import {
  buildMonthGrid,
  endOfDay,
  isSameDay,
  parseISODate,
  startOfDay,
  toISODate,
} from "../day";
import {
  formatLongDate,
  formatMonthYear,
  formatShortDate,
  mondayFirstWeekdayLabels,
} from "../format";
import { springSoft, useMotionTransition } from "../motion";
import { useSheetPortalContainer } from "./Sheet";

export interface DatePickerProps {
  /**
   * The selected date as an ISO date-only string (`YYYY-MM-DD`), or `""` when
   * nothing is selected. Deliberately a plain string rather than a `Date`:
   * this is the same wire format the create form's `damageAt` already uses,
   * and it sidesteps the timezone traps of round-tripping a `Date` through
   * midnight (a `Date` at local midnight can report the previous calendar day
   * once serialized to UTC).
   */
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  /**
   * Applied to the trigger button, so a `Field`'s `<label htmlFor>` points at
   * the element the user actually operates.
   */
  readonly id?: string;
  readonly "aria-label": string;
  readonly "aria-describedby"?: string;
  readonly "aria-invalid"?: boolean;
  readonly placeholder?: string;
  /** Earliest selectable date (`YYYY-MM-DD`, inclusive). */
  readonly min?: string;
  /** Latest selectable date (`YYYY-MM-DD`, inclusive). */
  readonly max?: string;
  readonly disabled?: boolean;
  /** Hide the clear button even when a date is selected. */
  readonly clearable?: boolean;
  /**
   * Compact trigger for cramped layouts (the period popover's side-by-side
   * date fields): an abbreviated month ("15 jun 2024") and no calendar icon.
   * Absent is byte-identical to before — the long format and the icon.
   */
  readonly compact?: boolean;
  readonly className?: string;
}

/** Formats a selected value for the trigger, e.g. `15 juni 2024`. */
function formatDisplay(value: string, compact: boolean): string | null {
  const date = parseISODate(value);
  if (!date) return null;
  return compact ? formatShortDate(date) : formatLongDate(date);
}

/**
 * Single-date picker built on Radix Popover with a hand-rolled month grid
 * (no calendar dependency). Values are ISO date-only strings; the visible
 * label and calendar are Dutch, Monday-first. Mirrors the Combobox primitive's
 * popover/motion/sheet-portal patterns so it drops into the same form layouts.
 *
 * `aria-required` is deliberately not supported: the trigger is a plain
 * button, a role that does not support it, so AT would ignore the attribute.
 * Supporting it means giving the trigger a composite role, which changes
 * what screen readers announce and needs its own testing.
 */
export function DatePicker({
  value,
  onValueChange,
  id,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  placeholder = "Kies een datum",
  min,
  max,
  disabled = false,
  clearable = true,
  compact = false,
  className,
}: DatePickerProps) {
  const popoverTransition = useMotionTransition(springSoft);
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => parseISODate(value), [value]);
  // The month currently on screen: the selected month, else today's.
  const [viewDate, setViewDate] = useState(() => selected ?? new Date());
  const sheetContainer = useSheetPortalContainer();

  const minDate = useMemo(() => parseISODate(min ?? ""), [min]);
  const maxDate = useMemo(() => parseISODate(max ?? ""), [max]);
  const today = useMemo(() => new Date(), []);

  const grid = useMemo(
    () => buildMonthGrid(viewDate.getFullYear(), viewDate.getMonth()),
    [viewDate],
  );

  const isOutOfRange = (date: Date): boolean => {
    if (minDate && date < startOfDay(minDate)) return true;
    if (maxDate && date > endOfDay(maxDate)) return true;
    return false;
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    // Re-anchor the visible month to the selection each time we open, so
    // reopening after picking a far-off date doesn't strand the user on a
    // stale month.
    if (nextOpen) {
      setViewDate(selected ?? new Date());
    }
  };

  const selectDate = (date: Date) => {
    if (isOutOfRange(date)) return;
    onValueChange(toISODate(date));
    setOpen(false);
  };

  const shiftMonth = (delta: number) => {
    setViewDate(
      (current) =>
        new Date(current.getFullYear(), current.getMonth() + delta, 1),
    );
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onValueChange("");
  };

  const display = formatDisplay(value, compact);

  // Right padding must cover only the controls that actually render: the
  // clear button (size-5, so pr-8) and, in the default variant, the calendar
  // icon (size-4 with a gap-1, so pr-10 over the pr-8 base). Compact has no
  // icon, so it only ever needs the clear-button room.
  const hasClear = clearable && display && !disabled;
  const hasIcon = !compact;
  const trailingPadding =
    hasClear && hasIcon
      ? "pr-14"
      : hasIcon
        ? "pr-10"
        : hasClear
          ? "pr-8"
          : "pr-3";

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <div className={cn("relative flex w-full", className)}>
        <PopoverPrimitive.Trigger asChild>
          <button
            type="button"
            id={id}
            disabled={disabled}
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
            aria-haspopup="dialog"
            aria-expanded={open}
            className={cn(
              "inline-flex h-10 w-full items-center rounded-xl border border-input bg-surface pl-3 text-sm text-foreground shadow-sm transition-colors hover:bg-muted/50 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/15 disabled:pointer-events-none disabled:opacity-50 aria-[invalid=true]:border-destructive",
              trailingPadding,
            )}
          >
            <span
              className={cn("truncate", !display && "text-muted-foreground")}
            >
              {display ?? placeholder}
            </span>
          </button>
        </PopoverPrimitive.Trigger>
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center gap-1">
          {clearable && display && !disabled && (
            <button
              type="button"
              aria-label={ariaLabel ? `Wissen: ${ariaLabel}` : "Wissen"}
              onClick={handleClear}
              className="pointer-events-auto grid size-5 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-3.5" aria-hidden="true" />
            </button>
          )}
          {compact ? null : (
            <CalendarDays
              className="size-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          )}
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <PopoverPrimitive.Portal
            forceMount
            container={sheetContainer ?? undefined}
          >
            <PopoverPrimitive.Content
              asChild
              forceMount
              align="start"
              sideOffset={4}
            >
              <m.div
                initial={{ opacity: 0, scale: 0.96, y: 4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97, y: 2 }}
                transition={popoverTransition}
                role="dialog"
                aria-label={ariaLabel}
                className="z-50 w-[17rem] overflow-hidden rounded-xl border border-border bg-popover p-3 text-popover-foreground shadow-lg focus:outline-none"
              >
                <div className="mb-2 flex items-center justify-between">
                  <button
                    type="button"
                    aria-label="Vorige maand"
                    onClick={() => shiftMonth(-1)}
                    className="grid size-7 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/15"
                  >
                    <ChevronLeft className="size-4" aria-hidden="true" />
                  </button>
                  <span
                    aria-live="polite"
                    className="text-sm font-medium capitalize text-foreground"
                  >
                    {formatMonthYear(viewDate)}
                  </span>
                  <button
                    type="button"
                    aria-label="Volgende maand"
                    onClick={() => shiftMonth(1)}
                    className="grid size-7 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/15"
                  >
                    <ChevronRight className="size-4" aria-hidden="true" />
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-0.5">
                  {mondayFirstWeekdayLabels.map((weekday, index) => (
                    <span
                      key={index}
                      className="grid h-8 place-items-center text-xs font-medium text-muted-foreground"
                    >
                      {weekday}
                    </span>
                  ))}
                  {grid.map((date) => {
                    const inMonth = date.getMonth() === viewDate.getMonth();
                    const isSelected = selected && isSameDay(date, selected);
                    const isToday = isSameDay(date, today);
                    const outOfRange = isOutOfRange(date);
                    return (
                      <button
                        key={toISODate(date)}
                        type="button"
                        disabled={outOfRange}
                        aria-label={formatLongDate(date)}
                        aria-current={isToday ? "date" : undefined}
                        aria-pressed={isSelected ?? undefined}
                        onClick={() => selectDate(date)}
                        className={cn(
                          "grid h-8 place-items-center rounded-lg text-sm tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/15 disabled:pointer-events-none disabled:opacity-30",
                          isSelected
                            ? "bg-primary font-semibold text-primary-foreground"
                            : cn(
                                "hover:bg-muted/50",
                                inMonth
                                  ? "text-foreground"
                                  : "text-muted-foreground",
                                isToday && "font-semibold text-primary",
                              ),
                        )}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>
              </m.div>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        )}
      </AnimatePresence>
    </PopoverPrimitive.Root>
  );
}
